import React, {ChangeEvent, ComponentProps, useEffect, useRef, useState} from 'react';
import classnames from 'classnames';
import {useIntl} from 'react-intl';
import invariant from 'tiny-invariant';
import {combine} from '@atlaskit/pragmatic-drag-and-drop/combine';
import {dropTargetForExternal, monitorForExternal} from '@atlaskit/pragmatic-drag-and-drop/external/adapter';
import {containsFiles, getFiles} from '@atlaskit/pragmatic-drag-and-drop/external/file';
import {preventUnhandled} from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import {EditableFileName} from './EditableFileName';
import Empty from '../Empty/Empty';
import ButtonUploadFile from '../ButtonUploadFile';
import {FilesHelper} from '../../utils/FilesHelper';
import {toAbsoluteUrl} from '../../../../../_metronic/_helpers';
import ButtonAction from '../ButtonAction';
import styles from './styles.module.scss';

interface DNDFileUploadProps extends ComponentProps<'input'> {
  onChanged: (files: File[]) => void;
  onEdit: (files: File[]) => void;
  files: File[];
  multiple: boolean;
}

const DNDFileUpload = ({onChanged, onEdit, files, disabled, multiple, ...rest}: DNDFileUploadProps) => {
  const paste = (evt: ClipboardEvent) => {
    if (!evt.clipboardData) return;

    const items = Array.from(evt.clipboardData.items).map(item => item.getAsFile()).filter(Boolean) as File[];
    if (items.length > 0) {
      onChanged(items);
    }
  };

  useEffect(() => {
    if (!disabled) {
      document.addEventListener('paste', paste);
    }

    return () => {
      document.removeEventListener('paste', paste);
    };
  }, [disabled]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target?.files || []);
    if (selectedFiles && selectedFiles.length > 0) {
      onChanged(selectedFiles);
      e.target.files = null;
      // @ts-ignore
      e.target.value = null;
    }
  };

  const handleFileNameBlur = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const file = files[index];
    const updatedFile = new File([file], rename(file.name, e.target.value), {type: file.type});
    onEdit([
      ...files.slice(0, index),
      updatedFile,
      ...files.slice(index + 1, files.length),
    ]);
  };

  const rename = (currentFileName: string, newFileName: string) => {
    const [/*name*/, extension] = currentFileName.split('.');
    return newFileName
      ? [newFileName, extension].join('.')
      : currentFileName;
  };

  const ref = useRef<HTMLDivElement | null>(null);
  const [/*state*/, setState] = useState<'idle' | 'potential' | 'over'>('idle');

  useEffect(() => {
    const el = ref.current;
    invariant(el);
    return combine(
      dropTargetForExternal({
        element: el,
        canDrop: containsFiles,
        onDragEnter: () => setState('over'),
        onDragLeave: () => setState('potential'),
        onDrop: async ({source}) => {
          if (disabled) return;
          const files = getFiles({source});
          onChanged(multiple ? [...files] : [files[0]]);
        },
      }),
      monitorForExternal({
        canMonitor: containsFiles,
        onDragStart: () => {
          setState('potential');
          preventUnhandled.start();
        },
        onDrop: () => {
          setState('idle');
          preventUnhandled.stop();
        },
      }),
    );
  });

  const intl = useIntl();
  const uploadButtonLabel = intl.formatMessage({
    id: 'upload-file',
    defaultMessage: 'Upload file',
  });
  const uploadFileDndLabel = intl.formatMessage({
    id: 'upload-file-dnd-label',
    defaultMessage: 'You can drag files directly here or paste them'
  });

  const handleRemoveFile = (files: File[], index: number) => {
    const rest = [...files.slice(0, index), ...files.slice(index + 1, files.length)];
    onEdit(rest);
  };

  return (
    <div className={styles.modalContainer}>
      <div
        ref={ref}
        className={classnames(styles.dragDropContainer, {[styles.dragDropDisabled]: disabled})}
      >
        <div className={styles.dragDropHeader}>
          <img
            src={toAbsoluteUrl('/media/common/Upload_file.svg')}
            alt="file" className={styles.dragDropIcon}
          />
          <div>{uploadFileDndLabel}</div>
        </div>
      </div>
      <ButtonUploadFile
        {...rest}
        onChange={handleFileChange}
        label={uploadButtonLabel}
        disabled={disabled}
        multiple={multiple}
        showIcon={true}
        customContainerStyles={'w-125px h-40px mt-8'}
      />
      <div className={styles.fileListContainer}>
        {files.length > 0
          ? (
            <>
              {files.map((file, index) => (
                <div className={styles.fileItem} key={index}>
                  <img
                    src={FilesHelper.getFileTypeIcon(file.type)}
                    alt={'type'}
                    className={styles.typeIcon}
                  />
                  <EditableFileName
                    fileName={file?.name}
                    onBlur={handleFileNameBlur(index)}
                  />
                  <ButtonAction
                    variant={'icon'}
                    iconButtonType={'error'}
                    avoidDoubleClick={false}
                    onClick={() => handleRemoveFile(files, index)}
                    label={
                      <img
                        src={toAbsoluteUrl('/media/common/Delete_red.svg')}
                        alt={'remove'}
                      />
                    }
                    customClassnames={'mx-2'}
                    disabled={false}
                  />
                </div>
              ))}
            </>
          )
          : <Empty/>}
      </div>
    </div>
  );
};

export default DNDFileUpload;